import DOMPurify from 'dompurify';

/**
 * HTML-encodes a string to prevent browser from parsing it as HTML
 */
const htmlEncode = (text: string): string => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

/**
 * Encodes invalid HTML tags (like <test one>, <FOR HK>) before sanitization
 * This preserves informal markup that would otherwise be stripped by the browser parser
 */
const encodeInvalidTags = (html: string): string => {
  if (!html) return '';
  
  // List of valid HTML tags we want to preserve
  const validTags = ['b', 'strong', 'span', 'br', 'p', 'div', 'em', 'font'];
  
  // Regex to match any HTML-like tag pattern: <...>
  // This includes valid tags, invalid tags with spaces, and informal markup
  const tagPattern = /<[^>]+>/g;
  
  return html.replace(tagPattern, (match) => {
    // Extract the tag name (first word after < or </)
    const tagNameMatch = match.match(/<\/?([a-zA-Z][a-zA-Z0-9]*)/);
    
    if (!tagNameMatch) {
      // If we can't extract a valid tag name, it's likely invalid markup - encode it
      return htmlEncode(match);
    }
    
    const tagName = tagNameMatch[1].toLowerCase();
    
    // If it's a valid tag, keep it as-is
    if (validTags.includes(tagName)) {
      return match;
    }
    
    // If it's an invalid tag (e.g., <test one>, <FOR HK>), HTML-encode it
    // This prevents the browser from trying to parse it as a tag
    return htmlEncode(match);
  });
};

/**
 * Sanitizes HTML for safe display while preserving text content
 * This prevents XSS while allowing informal markup like <FOR HK>, <=7.05 to display correctly
 * 
 * Strategy:
 * 1. First encode invalid HTML tags (like <test one>) so browser doesn't parse them
 * 2. Then sanitize with DOMPurify to remove dangerous content
 * 
 * Configuration:
 * - Preserves text content even when tags are removed (KEEP_CONTENT: true)
 * - Allows safe formatting tags (b, strong, span, br, p, div)
 * - Allows style attributes for formatting
 * - Strips dangerous tags (script, img, iframe, etc.)
 * 
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export const sanitizeHtmlForDisplay = (html: string): string => {
  if (!html) return '';
  
  // Step 1: Encode invalid tags (like <test one>) so browser doesn't try to parse them
  const withEncodedInvalidTags = encodeInvalidTags(html);
  
  // Step 2: Sanitize with DOMPurify to remove dangerous content
  const purifyConfig = {
    ALLOWED_TAGS: ['b', 'strong', 'span', 'br', 'p', 'div', 'em', 'font'], // Allow basic formatting tags
    ALLOWED_ATTR: ['style', 'size'], // Allow style and size attributes for formatting
    KEEP_CONTENT: true, // Preserve text content even when tags are removed
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false
  };
  
  return DOMPurify.sanitize(withEncodedInvalidTags, purifyConfig);
};

